import { User } from '@/type';
import { type as accountType } from '@/iam/account';
import {
  isSuperAdmin,
  isAdmin,
  isResolver,
  isReviewer,
} from '@/role/roles';
import { scope } from '@/iam/scope';
import recognition from './recognition';

function hasUserAccess(user: User): boolean {
  if (user.id === recognition.getUserId()) return true;
  if (recognition.isServiceProvider()) return true;
  if (recognition.getAccountId() !== user.accountId) return false;
  if (recognition.isSuperAdmin()) return true;
  if (recognition.isAdmin()) {
    return (!isSuperAdmin(user.role.type) && !isAdmin(user.role.type));
  }
  if (recognition.isManager()) {
    return (isResolver(user.role.type) || isReviewer(user.role.type));
  }
  return false;
}

export default {
  RESOLVE_POST: {
    scope: scope.POST_RESOLVE,
  },
  VIEW_POST_NOTES: {
    accountTypes: [accountType.SERVICE_PROVIDER, accountType.CUSTOMER],
    scope: scope.POST_READ,
  },
  TRASH_POST: {
    accountTypes: [accountType.SERVICE_PROVIDER, accountType.CUSTOMER],
    scope: scope.POST_WRITE,
  },
  VIEW_POST_ACTIONS: {
    accountTypes: [accountType.SERVICE_PROVIDER, accountType.CUSTOMER],
    scope: scope.POST_WRITE,
  },
  VIEW_POST: {
    scope: scope.POST_READ,
  },
  INVITE_USER: {
    accountTypes: [accountType.SERVICE_PROVIDER, accountType.CUSTOMER],
    scope: scope.USER_INVITE,
  },
  VIEW_USER_INVITATION_LINK: {
    accountTypes: [accountType.SERVICE_PROVIDER, accountType.CUSTOMER],
    scope: scope.USER_RESEND_INVITE,
  },
  TOGGLE_USER_SETUP_REMINDER: {
    scope: scope.USER_RESEND_INVITE,
  },
  EDIT_USER: {
    scope: scope.USER_EDIT,
    check: hasUserAccess,
  },
  DELETE_USER: {
    scope: scope.USER_DELETE,
    check(user: User): boolean {
      return (hasUserAccess(user) && recognition.getUserId() !== user.id);
    },
  },
  VIEW_USERS: {
    scope: scope.USER_READ,
  },
  ADD_CLIENT: {
    scope: scope.CLIENT_CREATE,
  },
  EDIT_CLIENT: {
    accountTypes: [accountType.SERVICE_PROVIDER],
    scope: scope.CLIENT_EDIT,
  },
  DELETE_CLIENT: {
    scope: scope.CLIENT_DELETE,
  },
  VIEW_CLIENTS: {
    accountTypes: [accountType.SERVICE_PROVIDER, accountType.CUSTOMER],
    scope: scope.CLIENT_READ,
  },
  VIEW_PAYMENT_METHODS: {
    scope: scope.BILLING_READ,
  },
  ADD_PAYMENT_METHOD: {
    scope: scope.PAYMENT_METHOD_WRITE,
  },
  DELETE_PAYMENT_METHOD: {
    scope: scope.PAYMENT_METHOD_WRITE,
  },
  EDIT_PAYMENT_METHOD: {
    scope: scope.PAYMENT_METHOD_WRITE,
  },
  SELECT_PAYMENT_METHOD: {
    scope: scope.BILLING_READ,
  },
  DELETE_SOCIAL_ACCOUNT: {
    scope: scope.SOCIAL_ACCOUNT_DELETE,
  },
  VIEW_PERSONS: {
    accountTypes: [accountType.CUSTOMER],
    scope: scope.ANALYSIS_FACES_READ,
  },
  MANAGE_PERSONS: {
    scope: scope.ANALYSIS_FACES_WRITE,
  },
  MANAGE_FACES: {
    scope: scope.ANALYSIS_FACES_WRITE,
  },
  VIEW_LABELS: {
    scope: scope.ANALYSIS_LABELS_READ,
  },
  VIEW_RESOURCES: {
    accountTypes: [accountType.SERVICE_PROVIDER, accountType.CUSTOMER],
  },
  ADMIN_MANAGEMENT: {
    accountTypes: [accountType.SERVICE_PROVIDER, accountType.CUSTOMER],
  },
  VIEW_LEGAL_PHRASES: {
    accountTypes: [accountType.SERVICE_PROVIDER, accountType.CUSTOMER],
  },
  VIEW_ACTIVE_ACCOUNT: {
    accountTypes: [accountType.SERVICE_PROVIDER, accountType.CUSTOMER],
  },
  VIEW_SERVICE_ACCOUNTS: {
    accountTypes: [accountType.SERVICE_PROVIDER, accountType.CUSTOMER],
    scope: scope.SERVICE_ACCOUNT_READ,
  },
};
